/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import API from 'utils/api';
import {
  baseURI,
  autorizarEntrevista,
  deleteEntrevista,
  getEntrevista,
  getEntrevistas,
  patchEntrevista,
  postEntrevista,
  getEntrevistasVistaoptimizado,
} from 'api/Entrevistas';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getCandidatovistaOptimized } from 'api/catalogos/Candidatos';
import moment from 'moment';
import EntrevistaModal from './EntrevistaModal';

export const niveldeestudioPermission = {
  permissionModel: 'niveldeestudio',
  catalogsModels: [],
  tabsModels: [],
};

const Entrevistas = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [pageActual, setPageActual] = useState(1);
  const [candidatos, setCandidatos] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getEntrevistas();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _candidatos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getCandidatovistaOptimized(),
        getEntrevistasVistaoptimizado(),
      ]);

      if (mounted) {
        setEstados(_estados);
        setCandidatos(_candidatos);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);

    if (match) {
      const entrevista = await getEntrevista(match.id);

      if (entrevista) {
        setSelectedData(entrevista);
        setModalVisible(true);
      }
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchEntrevista(key, values) : postEntrevista(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteEntrevista(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Nivel de Estudios ya autorizado');
        return;
      }
      const saved = await autorizarEntrevista(key);
      if (saved) {
        const _data = await getEntrevistas();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Candidato',
      align: 'center',
      children: [
        {
          title: 'Nombre',
          dataIndex: 'candidato_numero_y_nombre_completo',
          key: 'candidato_nombre',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Fuente de Reclutamiento',
          dataIndex: 'candidato_fuente_de_reclutamiento_descripcion',
          key: 'candidato_fuente',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Referente',
          dataIndex: 'candidato_referente',
          key: 'candidato_referente',
          width: 200,
          render: (val) => val ?? '—',
        },
        {
          title: 'Estatus',
          dataIndex: 'candidato_estado_del_candidato_descripcion',
          key: 'candidato_estado',
          width: 200,
          render: (val) => val ?? '—',
        },
      ],
    },
    {
      titleText: 'Cita',
      align: 'center',
      children: [
        {
          title: 'Fecha',
          dataIndex: 'cita_fecha_y_hora',
          key: 'cita_fecha',
          width: 180,
          render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '—'),
        },
        {
          title: 'Hora',
          dataIndex: 'cita_fecha_y_hora',
          key: 'cita_hora',
          width: 150,
          render: (val) => (val ? moment(val).format('HH:mm') : '—'),
        },
        {
          title: 'Estatus',
          dataIndex: 'cita_estado_de_cita_descripcion',
          key: 'cita_estado',
          width: 200,
          render: (val) => val ?? '—',
        },
      ],
    },
    {
      titleText: 'Vacante Solicitada',
      align: 'center',
      children: [
        {
          title: 'Fecha de Solicitud',
          dataIndex: 'vacante_fecha_de_solicitud',
          key: 'fecha_de_solicitud',
          width: 180,
          render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '—'),
        },
        {
          title: 'Folio',
          dataIndex: 'vacante__folio',
          key: 'folio',
          width: 120,
          render: (val) => val ?? '—',
        },
        {
          title: 'Área Vacante',
          dataIndex: 'vacante_directorio_de_area_clave_y_nombre',
          key: 'vacante_area',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Puesto Vacante',
          dataIndex: 'vacante_puesto_clave_y_descripcion',
          key: 'vacante_puesto',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Plaza Vacante',
          dataIndex: 'vacante_plaza_clave_y_descripcion',
          key: 'vacante_plaza',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Oficio',
          dataIndex: 'vacante_no_de_oficio',
          key: 'no_de_oficio',
          width: 200,
          render: (val) => val ?? '—',
        },
        {
          title: 'Motivo',
          dataIndex: 'vacante_motivo_de_reclutamiento_nombre',
          key: 'motivo',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Prioridad',
          dataIndex: 'vacante_prioridad_de_la_adquisicion_descripcion',
          key: 'vacante_prioridad_de_la_adquisicion_descripcion',
          width: 200,
        },
        {
          title: 'Fecha para Ocupar Vacante',
          dataIndex: 'vacante_fecha_para_ocupar_la_vacante',
          key: 'vacante_fecha_para_ocupar_la_vacante',
          width: 200,
          render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '—'),
        },
        {
          title: 'Estatus',
          dataIndex: 'vacante_estado_global_descripcion',
          key: 'vacante_estatus',
          width: 200,
          render: (val) => val ?? '—',
        },
      ],
    },
    {
      titleText: 'Solicitante',
      align: 'center',
      children: [
        {
          title: 'Solicitante',
          dataIndex: 'solicitante',
          key: 'solicitante',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Área Solicitante',
          dataIndex: 'solicitante_directorio_de_area_clave_y_nombre',
          key: 'solicitante_area',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Puesto Solicitante',
          dataIndex: 'solicitante_puesto_clave_y_descripcion',
          key: 'solicitante_puesto',
          width: 250,
          render: (val) => val ?? '—',
        },
        {
          title: 'Plaza Solicitante',
          dataIndex: 'solicitante_plaza_clave_y_descripcion',
          key: 'solicitante_plaza',
          width: 250,
          render: (val) => val ?? '—',
        },
      ],
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          pagination
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            // onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          // customActions={[
          //   {
          //     onClick: () => setVisibleAutorizar(true),
          //     icon: <CheckCircleOutlined />,
          //     text: 'Autorizar',
          //     disabledBySelection: true,
          //     disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
          //     className: 'success',
          //   },
          // ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />

        <EntrevistaModal
          isOpen={modalVisible}
          permission={permission}
          onClose={() => setModalVisible(false)}
          entrevista={selectedData}
        />

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Entrevista ${selectedData?.id}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la entrevista ${selectedData?.id}`}
          open={visibleAutorizar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Entrevistas;
